import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, message, Modal, Tag, Tooltip } from 'antd';
import { useProjectStore } from '../hooks/useProjectStore';
import { formationAPI, wellboreAPI, mechanicsAPI, loggingAPI, sweetSpotAPI } from '../services/api';
import DepthCursorOverlay from './sweetspot/DepthCursorOverlay';
import DepthValuePanel from './sweetspot/DepthValuePanel';
import type { DepthValueItem } from './sweetspot/DepthValuePanel';
import SweetSpotResultTable from './sweetspot/SweetSpotResultTable';
import SweetSpotSummaryCards from './sweetspot/SweetSpotSummaryCards';
import SweetSpotEvaluationControls from './sweetspot/SweetSpotEvaluationControls';
import type { SweetSpotReviewView } from './sweetspot/SweetSpotEvaluationControls';
import SweetSpotResultInspector from './sweetspot/SweetSpotResultInspector';
import SweetSpotHelp from './sweetspot/SweetSpotHelp';
import './sweetspot/SweetSpotWorkbench.css';
import {
  downloadSweetSpotCsv,
  SWEET_SPOT_CLASS_LABELS,
} from './sweetspot/sweetSpotExport';
import {
  loadParameterConfig,
  saveParameterConfig,
} from './sweetspot/sweetSpotDefaults';
import {
  buildParameterRanges, getEvaluationMode,
  normalizeWeights, resolveEffectiveParameters,
} from './sweetspot/sweetSpotUtils';
import {
  buildEvaluationInputSignature, createEvaluationRun, evaluateWithMethod,
  listEvaluationMethods, validateThresholds,
} from './sweetspot/sweetSpotEvaluationEngine';
import type {
  EvaluationMethodConfig, EvaluationRun,
} from './sweetspot/sweetSpotEvaluationEngine';
import {
  createDefaultMethodConfig, ensureSweetSpotMethodsRegistered,
} from './sweetspot/sweetSpotEvaluationMethods';
import {
  getActiveRunId, listEvaluationRuns, loadMethodConfig, saveEvaluationRun,
  saveMethodConfig, setActiveRunId, updateEvaluationRunStatus,
} from './sweetspot/sweetSpotEvaluationStorage';
import {
  adaptEvaluationRunToLegacyResult, buildBackendEvaluations,
} from './sweetspot/sweetSpotEvaluationAdapter';
import {
  CALCULATION_STATE_META, DATA_STATE_META, RESULT_STATE_META, TONE_TAG_COLORS,
  deriveCalculationState, deriveDataState, deriveSweetSpotWorkflowSteps, evaluationActionLabel,
  evaluationDisabledReason, getRunResultState,
} from './sweetspot/sweetSpotPresentation';
import type { SweetSpotWorkflowStepKey } from './sweetspot/sweetSpotPresentation';
import { sampleRows } from './sweetspot/sweetSpotCurveCatalog';
import { useSweetSpotWorkspaceStore } from './sweetspot/sweetSpotWorkspaceStore';
import { findNearestByDepth } from './sweetspot/sweetSpotDepth';
import { useDepthInteraction, useDepthWheel } from './sweetspot/useDepthInteraction';
import {
  DEFAULT_EQUAL_WEIGHT_NOTE, getReservoirTemplate, RESERVOIR_TEMPLATE_VERSION,
  RESERVOIR_TYPE_LABELS, RESERVOIR_TYPES, createParameterConfigFromTemplate,
  checkParameterConflicts, reservoirTypeLabel, PARAMETER_DEFINITIONS,
  buildParameterEvaluationConfigs, parameterDefinitionsForTemplate, RELATIVE_SCORING_NOTE,
} from './sweetspot/sweetSpotReservoir';
import { computeDataConfidence, computeEngineeringRisk } from './sweetspot/sweetSpotConfidence';
import {
  buildRecommendedParameters, SSI_METHOD_KEY, WEIGHT_SCHEME_LABELS,
} from './sweetspot/sweetSpotWeightSchemes';
import type { ReservoirType } from './sweetspot/types';
import type {
  Curve, Eval, GradeKey, Layer, Mech, Station,
  ParameterKey, SweetSpotEvaluationResult, SweetSpotInputPoint,
  SweetSpotParameterConfig, SweetSpotPointResult,
} from './sweetspot/types';

ensureSweetSpotMethodsRegistered();

// ═══════════════════════════════════════════════
// FracSight 甜点评价 — 仅水平段
// 按工作流步骤显示配置或结果；结果区展示测井曲线、RQ、CQ、SSI 与五级甜点评价。
//
// 当前数据流:
// 1. 地层/测斜/力学/测井/已保存评价分别从对应 API 加载并适配为页面类型。
// 2. 页面在水平段按 5m 采样，在 samples useMemo 中完成插值和甜点评分。
// 3. samples 供 LogTracks Canvas 展示和逐点 CSV 下载。
// 4. 点击计算时，按 40m 聚合后写入 sweetspot API。
// ═══════════════════════════════════════════════

const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.06)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  statBorder: '#ECEEF1', target: '#8B3A2A',
  gr: '#2E7D32', ac: '#6A1B9A', rt: '#C62828',
  por: '#1565C0', toc: '#5D4037', brit: '#B8860B',
  young: '#00695C', poisson: '#AD1457',
  geo: '#2874A6', eng: '#C2533A', comp: '#6A4C93',
};

// 甜点分级色 (Ⅰ优 / Ⅱ良 / Ⅲ一般 / Ⅳ差 / Ⅴ避射)
const GRADE = {
  I:   { label: 'Ⅰ', name: '优质', color: '#1E8449', bg: 'rgba(30,132,73,0.18)' },
  II:  { label: 'Ⅱ', name: '良好', color: '#2874A6', bg: 'rgba(40,116,166,0.16)' },
  III: { label: 'Ⅲ', name: '一般', color: '#D4770C', bg: 'rgba(212,119,12,0.16)' },
  IV:  { label: 'Ⅳ', name: '差',   color: '#C0392B', bg: 'rgba(192,57,43,0.14)' },
  V:   { label: 'Ⅴ', name: '避射', color: '#6C757D', bg: 'rgba(108,117,125,0.16)' },
};

const LITH_ZH: Record<string, string> = {
  sandstone: '砂岩', shale: '页岩', limestone: '灰岩', mudstone: '泥岩',
  dolomite: '白云岩', conglomerate: '砾岩', coal: '煤层', other: '其他',
};
const LITH_COLOR: Record<string, string> = {
  砾岩: '#C4A87A', 灰岩: '#9CAAB8', 白云岩: '#A8C4A8',
  泥岩: '#7A8C72', 页岩: '#5A7050', 砂岩: '#D4B886', 煤层: '#2A2A2A', 其他: '#707078',
};

const DEFAULT_VISUAL_WORKSPACE_HEIGHT = 560;
const MIN_VISUAL_WORKSPACE_HEIGHT = 300;
const MAX_VISUAL_WORKSPACE_HEIGHT = 900;

const REVIEW_VIEW_META: Record<SweetSpotReviewView, { title: string; description: string }> = {
  curves: { title: '甜点评价曲线', description: '测井曲线 · RQ / CQ / SSI · 五级评价' },
  engineering: { title: '工程分段方案', description: '高低品质井段 · 推荐段界 · 候选簇位置' },
  data: { title: '评价数据明细', description: '逐深度点 RQ / CQ / SSI 与甜点等级' },
};

const PARAMETER_CURVE_ALIASES: Partial<Record<ParameterKey, string[]>> = {
  toc: ['TOC'],
  porosity: ['POR', '孔隙度', 'CNL', 'PHI'],
  brittleness: ['BRIT', 'BI', 'BRITTLENESS'],
  clayContent: ['VSH', 'CLAY', 'VCL', 'CLAYCONTENT'],
  youngModulus: ['YM', 'YME', 'YOUNG', 'YOUNGMODULUS'],
  poissonRatio: ['PR', 'POIS', 'POISSON', 'POISSONRATIO'],
  minHorizontalStress: ['SHMIN', 'SIGMA_H', 'MINHORIZONTALSTRESS'],
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const norm = (v: number, lo: number, hi: number) => clamp((v - lo) / (hi - lo || 1), 0, 1);
const round2 = (v: number | null) => (v == null ? null : Math.round(v * 100) / 100);
const finiteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function loadSsiConfig(projectId?: number): EvaluationMethodConfig {
  const stored = loadMethodConfig(projectId, 'legacy-rule');
  const base = stored ?? createDefaultMethodConfig(SSI_METHOD_KEY, loadParameterConfig(projectId));
  const reservoirType = base.reservoirType ?? 'shale-gas';
  return {
    ...base,
    parameters: base.weightStrategy === 'fixed'
      ? buildRecommendedParameters(base.parameters, reservoirType)
      : base.parameters,
    combination: 'arithmetic_mean',
  };
}

function mapLayers(raw: any[]): Layer[] {
  return (raw || []).map((r) => {
    const lith = LITH_ZH[r.lithology ?? r.lith] ?? r.lithology ?? '其他';
    return {
      name: r.name ?? '未命名',
      top: Number(r.top_depth ?? r.top ?? 0),
      bottom: Number(r.bottom_depth ?? r.bottom ?? 0),
      color: r.color_code || r.color || LITH_COLOR[lith] || '#707078',
      lith,
      target: Boolean(r.is_target ?? r.target ?? false),
    };
  });
}
function mapSurvey(raw: any[]): Station[] {
  return (raw || []).map((r) => ({
    md: Number(r.md ?? r.measured_depth ?? 0),
    inc: Number(r.inclination ?? r.inc ?? 0),
    azi: Number(r.azimuth ?? r.azi ?? 0),
    tvd: Number(r.tvd ?? r.true_vertical_depth ?? 0),
  })).sort((a, b) => a.md - b.md);
}
function mapMech(raw: any[]): Mech[] {
  return (raw || []).flatMap((r) => {
    const depth = finiteNumber(r.depth);
    if (depth == null) return [];
    return [{
      depth,
      youngs: finiteNumber(r.youngs_modulus),
      poisson: finiteNumber(r.poissons_ratio),
      brit: finiteNumber(r.brittleness_index),
      sh: finiteNumber(r.sigma_h),
      shmax: finiteNumber(r.sigma_h_max),
    }];
  }).sort((a, b) => a.depth - b.depth);
}
function mapCurves(raw: any[]): Curve[] {
  return (raw || []).map((r) => ({
    mnemonic: String(r.mnemonic || '').toUpperCase(),
    unit: r.unit || '',
    points: (Array.isArray(r.data_points) ? r.data_points : [])
      .flatMap((p: any) => {
        const depth = finiteNumber(p.depth ?? p.d);
        const value = finiteNumber(p.value ?? p.v);
        return depth == null || value == null ? [] : [{ depth, value }];
      })
      .sort((a: any, b: any) => a.depth - b.depth),
  }));
}
function mapEvals(raw: any[]): Eval[] {
  return (raw || []).map((r) => ({
    top: Number(r.top_depth ?? 0),
    bottom: Number(r.bottom_depth ?? 0),
    grade: (r.grade as GradeKey) in GRADE ? (r.grade as GradeKey) : 'IV',
    score: Number(r.composite_score ?? 0),
  })).sort((a, b) => a.top - b.top);
}

// 线性插值: 在已排序的 {x,y} 序列上按 x 取 y
function interp(arr: { x: number; y: number | null }[], x: number): number | null {
  if (!arr.length) return null;
  if (x < arr[0].x || x > arr[arr.length - 1].x) return null;
  if (x === arr[0].x) return arr[0].y;
  if (x === arr[arr.length - 1].x) return arr[arr.length - 1].y;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i].x >= x) {
      const a = arr[i - 1], b = arr[i];
      if (a.y == null || b.y == null) return a.y ?? b.y;
      const t = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x);
      return a.y + t * (b.y - a.y);
    }
  }
  return null;
}

// 进程内缓存: 页面间切换即时渲染, 后台静默刷新, 不再每次转圈
const SS_CACHE: Record<number, {
  layers: Layer[]; survey: Station[]; mech: Mech[]; curves: Curve[]; evals: Eval[];
}> = {};

export default function SweetSpotPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const navigate = useNavigate();
  const setSharedParameterConfig = useSweetSpotWorkspaceStore((state) => state.setParameterConfig);
  const setEvaluationResult = useSweetSpotWorkspaceStore((state) => state.setEvaluationResult);

  const cached0 = projectId ? SS_CACHE[projectId] : undefined;
  const [layers, setLayers] = useState<Layer[]>(cached0?.layers ?? []);
  const [survey, setSurvey] = useState<Station[]>(cached0?.survey ?? []);
  const [mech, setMech] = useState<Mech[]>(cached0?.mech ?? []);
  const [curves, setCurves] = useState<Curve[]>(cached0?.curves ?? []);
  const [evals, setEvals] = useState<Eval[]>(cached0?.evals ?? []);
  const [loading, setLoading] = useState(!cached0);
  const [error, setError] = useState<string | null>(null);
  const [operationBusy, setOperationBusy] = useState(false);
  const methodKey = SSI_METHOD_KEY;
  const [methodConfig, setMethodConfig] = useState<EvaluationMethodConfig>(() => loadSsiConfig(projectId));
  const [reservoirType, setReservoirType] = useState<ReservoirType>(
    () => (methodConfig.reservoirType ?? 'shale-gas'),
  );
  const [pendingReservoirType, setPendingReservoirType] = useState<ReservoirType | null>(null);
  const [reservoirModalOpen, setReservoirModalOpen] = useState(false);
  const [draftRun, setDraftRun] = useState<EvaluationRun>();
  const [runHistory, setRunHistory] = useState<EvaluationRun[]>([]);
  const [activeRunId, setActiveRunIdState] = useState<string>();
  const [storageError, setStorageError] = useState<string>();
  const [activeWorkflowStep, setActiveWorkflowStep] = useState<SweetSpotWorkflowStepKey>('data-check');
  const [reviewView, setReviewView] = useState<SweetSpotReviewView>('curves');
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [showGradeBackground, setShowGradeBackground] = useState(true);
  const [showAnomalies, setShowAnomalies] = useState(false);

  // 参数配置按项目保存在浏览器本地；标准化曲线由数据中心应用到 logging API。
  useEffect(() => {
    const nextConfig = loadSsiConfig(projectId);
    setMethodConfig(nextConfig);
    setReservoirType(nextConfig.reservoirType ?? 'shale-gas');
    setDraftRun(undefined);
    setStorageError(undefined);
    setActiveWorkflowStep('data-check');
    setReviewView('curves');
    if (projectId) setSharedParameterConfig(projectId, nextConfig.parameters);
    if (projectId) {
      try {
        const activeId = getActiveRunId(projectId);
        setActiveRunIdState(activeId);
        listEvaluationRuns(projectId).then((items) => {
          const ssiRuns = items.filter((item) => item.methodKey === SSI_METHOD_KEY);
          setRunHistory(ssiRuns);
          const activeRun = items.find((item) => item.runId === activeId);
          if (!activeRun || activeRun.methodKey !== SSI_METHOD_KEY) return;
          setDraftRun(activeRun);
          setMethodConfig(nextConfig);
          if (activeRun.config.reservoirType) setReservoirType(activeRun.config.reservoirType);
        }).catch((reason) => setStorageError(reason instanceof Error ? reason.message : '读取评价历史失败。'));
      } catch (reason) {
        setStorageError(reason instanceof Error ? reason.message : '读取活动版本失败。');
      }
    } else {
      setActiveRunIdState(undefined);
      setRunHistory([]);
    }
  }, [projectId, setSharedParameterConfig]);

  // ── 储层类型切换：先检查未保存修改，提供 加载模板 / 保留当前 / 另存方案 / 取消 ──
  const requestReservoirTypeChange = (next: ReservoirType) => {
    if (next === reservoirType) return;
    setPendingReservoirType(next);
    setReservoirModalOpen(true);
  };

  const applyReservoirType = (next: ReservoirType, mode: 'load-template' | 'keep-parameters') => {
    const template = getReservoirTemplate(next);
    const existing = methodConfig;
    const configured: EvaluationMethodConfig = mode === 'load-template'
      ? {
        ...existing,
        parameters: createParameterConfigFromTemplate(template, existing.parameters),
        reservoirType: next,
        templateVersion: template.version,
        reviewState: undefined,
      }
      : {
        ...existing,
        reservoirType: next,
        templateVersion: template.version,
        reviewState: undefined,
      };
    const nextConfig: EvaluationMethodConfig = configured.weightStrategy === 'fixed'
      ? { ...configured, parameters: buildRecommendedParameters(configured.parameters, next) }
      : configured;
    setReservoirType(next);
    setMethodConfig(nextConfig);
    setDraftRun(undefined);
    if (projectId) {
      if (!saveMethodConfig(projectId, 'legacy-rule', nextConfig)) {
        message.warning('储层配置未能写入localStorage，本次切换仅在当前会话有效。');
      }
      saveParameterConfig(projectId, nextConfig.parameters);
      setSharedParameterConfig(projectId, nextConfig.parameters);
    }
    const note = mode === 'load-template'
      ? `已加载${RESERVOIR_TYPE_LABELS[next]}模板（${nextConfig.parameters.rq.length} RQ + ${nextConfig.parameters.cq.length} CQ，${DEFAULT_EQUAL_WEIGHT_NOTE}）。`
      : `已切换为${RESERVOIR_TYPE_LABELS[next]}，保留当前参数配置。`;
    message.success(note);
    setReservoirModalOpen(false);
    setPendingReservoirType(null);
  };

  const handleMethodConfigChange = (next: EvaluationMethodConfig) => {
    setMethodConfig(next);
    if (projectId) {
      setSharedParameterConfig(projectId, next.parameters);
      const methodSaved = saveMethodConfig(projectId, 'legacy-rule', next);
      const parametersSaved = saveParameterConfig(projectId, next.parameters);
      if (!methodSaved || !parametersSaved) {
        message.warning('评价配置未能写入浏览器存储，本次修改仅在当前会话有效。');
      }
    }
  };

  const enabledParameterCount = methodConfig.parameters.rq.filter((parameter) => parameter.enabled).length
    + methodConfig.parameters.cq.filter((parameter) => parameter.enabled).length;
  const recommendedParameters = useMemo(
    () => buildRecommendedParameters(methodConfig.parameters, reservoirType),
    [methodConfig.parameters, reservoirType],
  );

  // 数据来源: formation / wellbore / mechanics / logging / sweetspot 五个现有后端接口。
  // 已保存的 evals 目前只进入页面缓存；主图仍使用下方 samples 的前端实时计算结果。
  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    let cancelled = false;
    // 有缓存则不转圈(后台静默刷新); 无缓存才显示加载
    if (!SS_CACHE[projectId]) setLoading(true);
    setError(null);
    const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);
    const requests = [
      formationAPI.getLayers(projectId),
      wellboreAPI.getSurvey(projectId),
      mechanicsAPI.getProfile(projectId),
      loggingAPI.getCurves(projectId),
      sweetSpotAPI.list(projectId),
    ];
    const sourceLabels = ['地层', '井轨迹', '岩石力学', '测井曲线', '已保存评价'];
    Promise.allSettled(requests).then((results) => {
      if (cancelled) return;
      const cached = SS_CACHE[projectId];
      const failedSources: string[] = [];
      const readResult = (index: number): any[] | null => {
        const result = results[index];
        if (result.status === 'fulfilled') return un(result.value.data);
        failedSources.push(sourceLabels[index]);
        return null;
      };
      const layerRows = readResult(0), surveyRows = readResult(1), mechRows = readResult(2),
        curveRows = readResult(3), evalRows = readResult(4);
      const L = layerRows ? mapLayers(layerRows) : cached?.layers ?? [];
      const S = surveyRows ? mapSurvey(surveyRows) : cached?.survey ?? [];
      const M = mechRows ? mapMech(mechRows) : cached?.mech ?? [];
      const C = curveRows ? mapCurves(curveRows) : cached?.curves ?? [];
      const E = evalRows ? mapEvals(evalRows) : cached?.evals ?? [];
      setLayers(L); setSurvey(S); setMech(M); setCurves(C); setEvals(E);
      SS_CACHE[projectId] = { layers: L, survey: S, mech: M, curves: C, evals: E };
      setError(failedSources.length
        ? `${failedSources.join('、')}加载失败，已保留其余可用数据${cached ? '和本地缓存' : ''}。`
        : null);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId]);

  // 水平段 MD 区间: 第一个 inc>=85° 到井底
  const lateral = useMemo<[number, number] | null>(() => {
    if (!survey.length) return null;
    const start = survey.find((s) => s.inc >= 85);
    if (!start) return null;
    const end = survey[survey.length - 1];
    return [start.md, end.md];
  }, [survey]);

  // MD → TVD 插值
  const mdToTvd = useMemo(() => {
    const arr = survey.map((s) => ({ x: s.md, y: s.tvd as number | null }));
    return (md: number) => interp(arr, md) ?? md;
  }, [survey]);

  const curveByName = (names: string[]): Curve | undefined =>
    curves.find((c) => names.some((n) => c.mnemonic === n || c.mnemonic.includes(n)));

  // 计算位置: 使用数据中心应用到业务模块的标准化曲线，按 5m 网格插值。
  const samples = useMemo(() => {
    if (!lateral) return [];
    const layerAtTvd = (tvd: number) => layers.find((layer) => tvd >= layer.top && tvd < layer.bottom);
    const scoreSamples = (rawSamples: any[]) => {
      const inputs: SweetSpotInputPoint[] = rawSamples.map((sample) => ({
        depth: sample.md,
        values: sample.parameterValues ?? {
          toc: sample.toc,
          porosity: sample.por,
          brittleness: sample.brit,
          clayContent: sample.clayContent,
          youngModulus: sample.youngs,
          poissonRatio: sample.poisson,
          minHorizontalStress: sample.sh,
        },
      }));
      const scores = evaluateWithMethod(methodKey, inputs, methodConfig).points;
      return rawSamples.map((sample, index) => {
        const score = scores[index];
        return {
          ...sample,
          parameterValues: inputs[index].values,
          rq: score.rq,
          cq: score.cq,
          ssi: score.ssi,
          grade: score.grade,
          // 兼容现有 Canvas 绘图键。
          geo: score.rq,
          eng: score.cq,
          comp: score.ssi,
          compress: score.ssi,
        };
      });
    };

    const [a, b] = lateral;
    const step = 5;
    const grGc = curveByName(['GR']);
    const acGc = curveByName(['AC', 'DT']);
    const rtGc = curveByName(['RT', 'RD', 'RES']);
    const porGc = curveByName(['POR', '孔隙度', 'CNL', 'PHI']);
    const tocGc = curveByName(['TOC']);
    const clayGc = curveByName(['VSH', 'CLAY', 'VCL', 'CLAYCONTENT']);
    const britGc = curveByName(['BRIT', 'BI', 'BRITTLENESS']);
    const youngsGc = curveByName(['YM', 'YME', 'YOUNG', 'YOUNGMODULUS']);
    const poissonGc = curveByName(['PR', 'POIS', 'POISSON', 'POISSONRATIO']);
    const shMinGc = curveByName(['SHMIN', 'SIGMA_H', 'MINHORIZONTALSTRESS']);

    const cAt = (c: Curve | undefined, md: number) =>
      c ? interp(c.points.map((p) => ({ x: p.depth, y: p.value as number | null })), md) : null;
    const customCurveByKey = new Map(
      [...methodConfig.parameters.rq, ...methodConfig.parameters.cq]
        .filter((parameter) => parameter.custom && parameter.sourceColumnKey)
        .map((parameter) => [
          parameter.key,
          curveByName([parameter.sourceColumnKey!.toUpperCase()]),
        ] as const),
    );

    const mArr = (k: keyof Mech) => mech.map((m) => ({ x: m.depth, y: m[k] as number | null }));
    const youngsA = mArr('youngs'), poissonA = mArr('poisson'), britA = mArr('brit'),
      shA = mArr('sh'), shmaxA = mArr('shmax');

    const rawSamples: any[] = [];
    for (let md = a; md <= b; md += step) {
      const tvd = mdToTvd(md);
      const layer = layerAtTvd(tvd);
      const gr = cAt(grGc, md), ac = cAt(acGc, md), rt = cAt(rtGc, md);
      const por = cAt(porGc, md);
      const toc = cAt(tocGc, md);
      const clayContent = cAt(clayGc, md);
      const brit = cAt(britGc, md) ?? interp(britA, md);
      const youngs = cAt(youngsGc, md) ?? interp(youngsA, md);
      const poisson = cAt(poissonGc, md) ?? interp(poissonA, md);
      const sh = cAt(shMinGc, md) ?? interp(shA, md);
      const shmax = interp(shmaxA, md);
      const stressDiff = sh != null && shmax != null ? shmax - sh : null;
      const parameterValues: SweetSpotInputPoint['values'] = {
        toc,
        porosity: por,
        brittleness: brit,
        clayContent,
        youngModulus: youngs,
        poissonRatio: poisson,
        minHorizontalStress: sh,
      };
      customCurveByKey.forEach((curve, key) => {
        parameterValues[key] = cAt(curve, md);
      });

      rawSamples.push({
        md, tvd, layer, gr, ac, rt, por, toc, clayContent,
        brit, youngs, poisson, sh, stressDiff, parameterValues,
      });
    }

    return scoreSamples(rawSamples);
  }, [lateral, mech, curves, layers, mdToTvd, methodConfig, methodKey]);

  const evaluationInputs = useMemo<SweetSpotInputPoint[]>(() => samples.map((sample) => ({
    depth: sample.md, values: sample.parameterValues,
  })), [samples]);
  const previewOutput = useMemo(
    () => evaluateWithMethod(methodKey, evaluationInputs, methodConfig),
    [evaluationInputs, methodConfig, methodKey],
  );
  const displayResults = useMemo<SweetSpotPointResult[]>(() => draftRun?.points ?? [], [draftRun]);
  const visualSamples = useMemo(() => samples.map((sample, index) => {
    const score = displayResults[index];
    return score ? {
      ...sample, rq: score.rq, cq: score.cq, ssi: score.ssi, grade: score.grade,
      geo: score.rq, eng: score.cq, comp: score.ssi, compress: score.ssi,
    } : {
      ...sample, rq: null, cq: null, ssi: null, grade: null,
      geo: null, eng: null, comp: null, compress: null,
    };
  }), [displayResults, samples]);

  const participatingSourceCurveIds = useMemo(() => {
    const ranges = buildParameterRanges(samples.map((sample) => ({
      depth: sample.md,
      values: sample.parameterValues,
    })));
    const participatingParameterIds = new Set<string>();
    const effectiveParameters = (['rq', 'cq'] as const).flatMap((group) => {
      const effectiveForGroup = resolveEffectiveParameters(methodConfig.parameters[group], ranges)
        .map(({ parameter }) => parameter);
      samples.forEach((sample) => {
        const available = effectiveForGroup.filter((parameter) => {
          const value = sample.parameterValues[parameter.key];
          return typeof value === 'number' && Number.isFinite(value);
        });
        const weights = normalizeWeights(available);
        available.forEach((parameter) => {
          if ((weights[parameter.id] ?? 0) > 0) participatingParameterIds.add(parameter.id);
        });
      });
      return effectiveForGroup;
    }).filter((parameter) => participatingParameterIds.has(parameter.id));
    return [...new Set(effectiveParameters.flatMap((parameter) => {
      const aliases = parameter.sourceColumnKey
        ? [parameter.sourceColumnKey.toUpperCase()]
        : PARAMETER_CURVE_ALIASES[parameter.key] ?? [];
      const sourceCurve = curveByName(aliases);
      return sourceCurve?.points.length ? [sourceCurve.mnemonic] : [];
    }))];
  }, [curves, methodConfig.parameters, samples]);

  const methodDefinition = useMemo(() => listEvaluationMethods().find(
    (item) => item.methodId + '@' + item.version === methodKey,
  )!, [methodKey]);
  const inputSignature = useMemo(
    () => buildEvaluationInputSignature(evaluationInputs, participatingSourceCurveIds),
    [evaluationInputs, participatingSourceCurveIds],
  );
  const dataState = deriveDataState(curves.length + mech.length, previewOutput.quality);
  const resultState = getRunResultState({
    run: draftRun,
    activeRunId,
    methodKey,
    definition: methodDefinition,
    config: methodConfig,
    points: evaluationInputs,
    sourceCurveIds: participatingSourceCurveIds,
  });
  const calculationState = deriveCalculationState({
    busy: operationBusy,
    resultState,
  });
  const configValid = validateThresholds(methodConfig.gradeThresholds)
    && enabledParameterCount > 0;
  const computeReason = evaluationDisabledReason({
    busy: operationBusy,
    dataState,
    quality: previewOutput.quality,
    hasInterval: Boolean(lateral),
    configValid,
  });
  const structuredResult = useMemo(() => {
    if (!draftRun) return null;
    return adaptEvaluationRunToLegacyResult(draftRun);
  }, [
    draftRun,
  ]);
  const hasEvaluationResult = Boolean(draftRun && displayResults.length);
  const resultIsStale = ['parameters_changed', 'data_changed', 'method_changed'].includes(resultState);
  const hasSavedResult = Boolean(draftRun && draftRun.status !== 'draft' && !resultIsStale);
  const workflowSteps = useMemo(() => deriveSweetSpotWorkflowSteps({
    dataState,
    calculationState,
    configValid,
    canCalculate: !computeReason,
    hasResult: hasEvaluationResult,
    canSaveResult: Boolean(draftRun?.status === 'draft' && draftRun.quality.canSaveFormal && !resultIsStale),
    hasSavedResult,
    canApplyResult: Boolean(draftRun?.quality.canActivate && !resultIsStale),
    isActiveResult: Boolean(draftRun && draftRun.runId === activeRunId && !resultIsStale),
    reviewState: methodConfig.reviewState,
  }), [
    activeRunId, calculationState, computeReason, configValid, dataState, draftRun,
    hasEvaluationResult, hasSavedResult, methodConfig.reviewState, resultIsStale,
  ]);
  const parameterCoverage = useMemo(() => Object.fromEntries(
    [...methodConfig.parameters.rq, ...methodConfig.parameters.cq].map((parameter) => {
      const validCount = samples.filter((sample) => {
        const value = sample.parameterValues[parameter.key];
        return typeof value === 'number' && Number.isFinite(value);
      }).length;
      return [parameter.id, samples.length ? validCount / samples.length : 0];
    }),
  ), [methodConfig.parameters, samples]);
  const sourceCurveByParameterId = useMemo(() => Object.fromEntries(
    [...methodConfig.parameters.rq, ...methodConfig.parameters.cq].map((parameter) => {
      const aliases = parameter.sourceColumnKey
        ? [parameter.sourceColumnKey.toUpperCase()]
        : PARAMETER_CURVE_ALIASES[parameter.key] ?? [];
      const source = curves.find((curve) => aliases.some(
        (alias) => curve.mnemonic === alias || curve.mnemonic.includes(alias),
      ));
      return [parameter.id, source?.mnemonic];
    }),
  ), [curves, methodConfig.parameters]);

  // ── 储层模板下的数据置信度与工程风险摘要 ──
  const reservoirTemplate = useMemo(() => getReservoirTemplate(reservoirType), [reservoirType]);
  const reservoirConflicts = useMemo(
    () => checkParameterConflicts([
      ...reservoirTemplate.rqParameterCodes,
      ...reservoirTemplate.cqParameterCodes,
    ].filter((code) => {
      const definition = PARAMETER_DEFINITIONS[code];
      const id = `${definition?.dimension.toLowerCase()}.${code.toLowerCase()}`;
      return (parameterCoverage[id] ?? 0) > 0;
    })),
    [reservoirTemplate, parameterCoverage],
  );
  const dataConfidenceSummary = useMemo(() => {
    const codes = [...reservoirTemplate.rqParameterCodes, ...reservoirTemplate.cqParameterCodes];
    let requiredTotal = 0;
    let requiredCovered = 0;
    let recommendedTotal = 0;
    let recommendedCovered = 0;
    let matchedCount = 0;
    codes.forEach((code) => {
      const definition = PARAMETER_DEFINITIONS[code];
      if (!definition) return;
      const id = `${definition.dimension.toLowerCase()}.${code.toLowerCase()}`;
      const coverage = parameterCoverage[id] ?? 0;
      if (definition.requirement === 'required') {
        requiredTotal += 1;
        if (coverage > 0) requiredCovered += 1;
      }
      if (definition.requirement === 'recommended') {
        recommendedTotal += 1;
        if (coverage > 0) recommendedCovered += 1;
      }
      if (sourceCurveByParameterId[id]) matchedCount += 1;
    });
    return computeDataConfidence({
      requiredCompleteness: requiredTotal ? requiredCovered / requiredTotal : 1,
      recommendedCompleteness: recommendedTotal ? recommendedCovered / recommendedTotal : 1,
      matchRatio: codes.length ? matchedCount / codes.length : 1,
      unitValidRatio: 1,
      abnormalRatio: 0,
      alternativeRatio: 0,
      manualRatio: 0,
      hasBlockThresholds: false,
    });
  }, [reservoirTemplate, parameterCoverage, sourceCurveByParameterId]);
  const engineeringRiskSummary = useMemo(() => {
    const riskDefinitions = parameterDefinitionsForTemplate(reservoirTemplate)
      .filter((definition) => definition.direction === 'risk');
    const ranges = buildParameterRanges(evaluationInputs);
    const aggregate = evaluationInputs.map((point) => computeEngineeringRisk(
      point.values, riskDefinitions, ranges,
    ));
    const scored = aggregate.filter((item) => item.items.length > 0);
    if (!scored.length) return { score: 0, level: 'low' as const, items: [] };
    return {
      score: scored.reduce((sum, item) => sum + item.score, 0) / scored.length,
      level: scored[0].level,
      items: scored[0].items,
    };
  }, [reservoirTemplate, evaluationInputs]);

  const chartSamples = useMemo(() => sampleRows(visualSamples, 2000), [visualSamples]);
  const evaluationMode = getEvaluationMode(
    displayResults.some((result) => result.rq != null) ? 0 : null,
    displayResults.some((result) => result.cq != null) ? 0 : null,
  );

  const sampleDepths = useMemo(() => visualSamples.map((sample) => sample.md), [visualSamples]);
  const depthInteraction = useDepthInteraction(sampleDepths, projectId);
  const viewRange: [number, number] = depthInteraction.visibleRange ?? lateral ?? [0, 1];
  const logChartRef = useRef<HTMLDivElement | null>(null);
  const resizeCleanupRef = useRef<() => void>(() => undefined);
  const [visualWorkspaceHeight, setVisualWorkspaceHeight] = useState(DEFAULT_VISUAL_WORKSPACE_HEIGHT);
  const [visualResizeMode, setVisualResizeMode] = useState<'height' | null>(null);
  useDepthWheel(logChartRef, depthInteraction.zoomAt, depthInteraction.canNavigate, 34);

  useEffect(() => () => resizeCleanupRef.current(), []);

  const adjustVisualWorkspaceHeight = (delta: number) => {
    setVisualWorkspaceHeight((current) => Math.min(
      MAX_VISUAL_WORKSPACE_HEIGHT,
      Math.max(MIN_VISUAL_WORKSPACE_HEIGHT, current + delta),
    ));
  };

  const resetVisualWorkspaceBounds = () => {
    setVisualWorkspaceHeight(DEFAULT_VISUAL_WORKSPACE_HEIGHT);
  };

  const startVisualResize = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resizeCleanupRef.current();

    const startY = event.clientY;
    const startHeight = visualWorkspaceHeight;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    setVisualResizeMode('height');

    let finished = false;
    const handleMove = (moveEvent: PointerEvent) => {
      setVisualWorkspaceHeight(Math.min(
        MAX_VISUAL_WORKSPACE_HEIGHT,
        Math.max(MIN_VISUAL_WORKSPACE_HEIGHT, startHeight + moveEvent.clientY - startY),
      ));
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      setVisualResizeMode(null);
      resizeCleanupRef.current = () => undefined;
    };

    resizeCleanupRef.current = finish;
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };
  const selectedSample = useMemo(
    () => findNearestByDepth(visualSamples, depthInteraction.selectedDepth, (sample) => sample.md),
    [depthInteraction.selectedDepth, visualSamples],
  );
  const selectedDepthItems = useMemo<DepthValueItem[]>(() => {
    const grade = selectedSample?.grade as GradeKey | null | undefined;
    const customItems: DepthValueItem[] = [...methodConfig.parameters.rq, ...methodConfig.parameters.cq]
      .filter((parameter) => parameter.custom && parameter.enabled)
      .map((parameter) => ({
        label: `${parameter.label}（自定义${parameter.group.toUpperCase()}指标）`,
        section: parameter.group === 'rq' ? '储层参数' : '力学参数',
        value: selectedSample?.parameterValues?.[parameter.key],
        unit: parameter.unit,
        precision: 3,
      }));
    return [
      { section: '评价结果', label: '五级甜点评价', value: grade ? SWEET_SPOT_CLASS_LABELS[grade] : null, color: grade ? GRADE[grade].color : undefined },
      { section: '评价结果', label: '甜点指数 SSI', value: selectedSample?.ssi, precision: 3, color: TH.comp },
      { section: '评价结果', label: 'RQ（储层品质）', value: selectedSample?.rq, precision: 3, color: TH.geo },
      { section: '评价结果', label: '工程品质 CQ', value: selectedSample?.cq, precision: 3, color: TH.eng },
      { section: '储层参数', label: 'TOC（总有机碳）', value: selectedSample?.toc, unit: '%', precision: 2 },
      { section: '储层参数', label: '孔隙度', value: selectedSample?.por, unit: '%', precision: 2 },
      { section: '储层参数', label: 'BI（脆性指数）', value: selectedSample?.brit, unit: '%', precision: 2 },
      { section: '储层参数', label: '黏土含量', value: selectedSample?.clayContent, unit: '%', precision: 2 },
      { section: '力学参数', label: 'YM（杨氏模量）', value: selectedSample?.youngs, unit: 'GPa', precision: 2 },
      { section: '力学参数', label: 'PR（泊松比）', value: selectedSample?.poisson, precision: 3 },
      { section: '力学参数', label: 'Shmin（最小水平主应力）', value: selectedSample?.sh, unit: 'MPa', precision: 2 },
      ...customItems,
    ];
  }, [methodConfig.parameters, selectedSample]);

  const refreshRunHistory = () => {
    if (!projectId) return;
    listEvaluationRuns(projectId).then((items) => (
      setRunHistory(items.filter((item) => item.methodKey === SSI_METHOD_KEY))
    )).catch((reason) => {
      setStorageError(reason instanceof Error ? reason.message : '读取评价版本历史失败。');
    });
  };

  // 计算只创建内存草稿，不写后端、不写IndexedDB、也不改变下游活动结果。
  const handleCompute = () => {
    if (!projectId || !previewOutput.quality.canCalculate) {
      message.warning('当前数据覆盖率不满足计算要求。');
      return;
    }
    const run = createEvaluationRun({
      projectId,
      wellId: currentProject?.well_name || String(projectId),
      methodKey,
      config: methodConfig,
      output: previewOutput,
      sourceCurveIds: participatingSourceCurveIds,
      inputSignature,
    });
    setDraftRun(run);
    setStorageError(undefined);
    setActiveWorkflowStep('review');
    message.success('评价计算完成，结果状态为草稿；当前版本未变更。');
  };

  const handleSaveRun = async () => {
    if (!draftRun || draftRun.status !== 'draft') {
      message.warning('尚未生成可保存的评价结果。');
      return;
    }
    if (!draftRun.quality.canSaveFormal) {
      message.warning('当前草稿未通过正式保存检查。');
      return;
    }
    setOperationBusy(true);
    try {
      const saved = await saveEvaluationRun(draftRun);
      setDraftRun(saved);
      setRunHistory((current) => [saved, ...current.filter((run) => run.runId !== saved.runId)]);
      setStorageError(undefined);
      setActiveWorkflowStep('apply');
      message.success('评价版本已保存；当前版本未变更。');
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : '保存评价版本失败。';
      setStorageError(detail);
      message.error(detail);
    } finally {
      setOperationBusy(false);
    }
  };

  const handleActivateRun = async (run: EvaluationRun) => {
    if (!projectId || run.projectId !== projectId) return;
    if (run.status === 'draft') {
      message.warning('草稿状态不可设为当前版本。');
      return;
    }
    if (!run.quality.canActivate) {
      message.warning('储层品质 RQ 或工程品质 CQ 数据不足，不能应用为完整 SSI 评价版本。');
      return;
    }
    const evaluations = buildBackendEvaluations(run.points);
    const legacyView = adaptEvaluationRunToLegacyResult(run);
    if (!evaluations.length || !legacyView) {
      message.warning('该运行不能生成旧压裂设计所需的兼容结果。');
      return;
    }
    setOperationBusy(true);
    try {
      await sweetSpotAPI.batchImport({ project_id: projectId, evaluations });
      // 后端替换式同步成功后才更新本地活动指针。
      setActiveRunId(projectId, run.runId);
      const active = await updateEvaluationRunStatus(run.runId, 'active', {
        activatedAt: new Date().toISOString(), syncError: undefined,
      });
      setActiveRunIdState(run.runId);
      setDraftRun(active);
      setEvaluationResult(projectId, adaptEvaluationRunToLegacyResult(active)!);
      refreshRunHistory();
      setStorageError(undefined);
      setActiveWorkflowStep('apply');
      message.success('评价版本同步完成，已设为当前版本。');
      sweetSpotAPI.list(projectId).then(({ data }) => {
        setEvals(mapEvals(Array.isArray(data) ? data : data?.results ?? []));
      }).catch(() => message.warning('当前版本已同步，评价区间列表刷新失败。'));
    } catch (reason: any) {
      const response = reason?.response?.data;
      const detail = typeof response === 'string' ? response : response?.detail
        || (reason instanceof Error ? reason.message : '后端同步失败。');
      try {
        const failed = await updateEvaluationRunStatus(run.runId, 'syncFailed', { syncError: detail });
        setDraftRun(failed);
      } catch {
        // 主错误仍需显示；状态写入失败由历史刷新或下一次重试继续处理。
      }
      setStorageError(`设为当前版本失败：${detail}。当前版本未变更，可在历史版本中重试。`);
      message.error(`设为当前版本失败：${detail}`);
      refreshRunHistory();
    } finally {
      setOperationBusy(false);
    }
  };

  const handleSelectRun = (run: EvaluationRun) => {
    if (run.methodKey !== SSI_METHOD_KEY) return;
    setMethodConfig(run.config);
    if (run.config.reservoirType) setReservoirType(run.config.reservoirType);
    setDraftRun(run);
    setActiveWorkflowStep('review');
  };

  const handleProceedToDesign = () => {
    if (!activeRunId) {
      message.warning('未设置当前评价版本。');
      return;
    }
    navigate('/fracture/builder');
  };

  // ── 下载: 与页面结果表完全一致的逐点 CSV ──
  const handleDownload = () => {
    if (!displayResults.length) { message.warning('无甜点评价结果可导出'); return; }
    downloadSweetSpotCsv(displayResults);
    message.success('已导出 sweet-spot-results.csv');
  };

  const currentStatusMeta = operationBusy
    ? CALCULATION_STATE_META.running
    : resultState !== 'none'
      ? RESULT_STATE_META[resultState]
      : dataState !== 'complete'
        ? DATA_STATE_META[dataState]
        : { label: '评价准备就绪', tone: 'current' as const };
  const parameterLabelById = Object.fromEntries(
    [...methodConfig.parameters.rq, ...methodConfig.parameters.cq]
      .map((parameter) => [parameter.id, parameter.label]),
  );
  const missingParameterLabels = previewOutput.quality.missingIndicatorIds
    .map((parameterId) => parameterLabelById[parameterId] || parameterId);
  const currentStatusDetail = (
    <div className="sweetspot-status-popover">
      <div><span>数据完整度</span><strong>{(previewOutput.quality.completeness * 100).toFixed(1)}%</strong></div>
      <div><span>缺失参数</span><strong>{missingParameterLabels.length ? missingParameterLabels.join('、') : '无'}</strong></div>
      <div><span>评价结果</span><strong>{RESULT_STATE_META[resultState].label}</strong></div>
    </div>
  );

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>未选择项目</div>;
  // 不再阻塞性"加载数据中…"; 数据没到位时白屏一瞬, 二次访问走缓存秒开
  if (!lateral) return (
    <div className="sweetspot-empty-state">
      <div>
      <div style={{ marginBottom: 12 }}>
        {loading
          ? '正在加载已应用曲线…'
          : error || '未检测到可评价的水平段或标准化曲线。'}
      </div>
      <div style={{ marginBottom: 16, fontSize: 13, lineHeight: 1.7 }}>
        数据中心未检测到可用于当前项目的已应用标准化曲线。
      </div>
      <Button type="primary" onClick={() => navigate('/datahub/assets')}>选择已应用曲线</Button>
      </div>
    </div>
  );

  return (
    <div className="sweetspot-workbench" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <header className="sweetspot-context-bar">
        <div className="sweetspot-context-main">
          <h2 className="sweetspot-context-title">甜点评价</h2>
          <dl className="sweetspot-context-grid">
            <div><dt>项目名称</dt><dd>{currentProject?.name || projectId}</dd></div>
            <div><dt>井号</dt><dd>{currentProject?.well_name || projectId}</dd></div>
            <div><dt>评价区间</dt><dd>MD {lateral[0].toFixed(1)}–{lateral[1].toFixed(1)} m</dd></div>
            <div><dt>评价方法</dt><dd>甜点指数 SSI</dd></div>
            <div><dt>赋权方式</dt><dd>{WEIGHT_SCHEME_LABELS[methodConfig.weightStrategy]}</dd></div>
          </dl>
        </div>
        <div className="sweetspot-context-actions">
          <Tooltip title={currentStatusDetail} placement="bottomRight">
            <Tag className="sweetspot-current-status" color={TONE_TAG_COLORS[currentStatusMeta.tone]}>当前状态 · {currentStatusMeta.label}</Tag>
          </Tooltip>
          {reservoirConflicts.length > 0 && (
            <Tooltip title={reservoirConflicts.map((conflict) => conflict.message).join('\n')}>
              <Tag color="warning">重复加权风险</Tag>
            </Tooltip>
          )}
          <Button className="sweetspot-context-action is-data" size="small" onClick={() => setActiveWorkflowStep('data-check')}>数据检查</Button>
          <Tooltip title={hasEvaluationResult ? undefined : '执行评价后可导出结果'}><span tabIndex={hasEvaluationResult ? undefined : 0}><Button className="sweetspot-context-action is-export" size="small" onClick={handleDownload} disabled={!hasEvaluationResult}>导出结果</Button></span></Tooltip>
          <Button className="sweetspot-context-action is-method" size="small" onClick={() => setActiveWorkflowStep('method')}>评价指标</Button>
        </div>
      </header>

      <div className="sweetspot-workflow-frame">
        <SweetSpotEvaluationControls
          definitions={[methodDefinition]}
          config={methodConfig}
          recommendedParameters={recommendedParameters}
          quality={previewOutput.quality}
          draftRun={draftRun}
          history={runHistory}
          activeRunId={activeRunId}
          busy={operationBusy}
          storageError={storageError}
          calculationState={calculationState}
          dataState={dataState}
          resultState={resultState}
          computeLabel={evaluationActionLabel(calculationState)}
          computeReason={computeReason}
          activeStep={activeWorkflowStep}
          workflowSteps={workflowSteps}
          enabledParameterCount={enabledParameterCount}
          configValid={configValid}
          intervalLabel={`MD ${lateral[0].toFixed(1)}–${lateral[1].toFixed(1)} m`}
          parameterCoverage={parameterCoverage}
          sourceCurveByParameterId={sourceCurveByParameterId}
          availableSourceCurves={curves.filter((curve) => curve.points.length > 0).map((curve) => ({
            value: curve.mnemonic,
            label: curve.unit ? `${curve.mnemonic}（${curve.unit}）` : curve.mnemonic,
            unit: curve.unit,
          }))}
          reservoirType={reservoirType}
          reservoirOptions={RESERVOIR_TYPES.map((type) => ({ value: type, label: RESERVOIR_TYPE_LABELS[type] }))}
          showRecommendations={showRecommendations}
          showGradeBackground={showGradeBackground}
          showAnomalies={showAnomalies}
          reviewView={reviewView}
          onStepChange={setActiveWorkflowStep}
          onReviewViewChange={setReviewView}
          onReservoirTypeChange={requestReservoirTypeChange}
          onToggleRecommendations={setShowRecommendations}
          onToggleGradeBackground={setShowGradeBackground}
          onToggleAnomalies={setShowAnomalies}
          onProceedToDesign={handleProceedToDesign}
          onConfigChange={handleMethodConfigChange}
          onCompute={handleCompute}
          onSave={handleSaveRun}
          onActivate={handleActivateRun}
          onSelectRun={handleSelectRun}
          onRefreshHistory={refreshRunHistory}
        />

        {activeWorkflowStep === 'review' && <div className={`sweetspot-review-results${reviewView === 'curves' ? '' : ' is-detail-view'}`}>
        <main className="sweetspot-analysis-column">
          <div className="sweetspot-analysis-header">
            <div>
              <strong>{REVIEW_VIEW_META[reviewView].title}</strong>
              <span>{REVIEW_VIEW_META[reviewView].description}</span>
            </div>
            <div className="sweetspot-analysis-header-actions">
              {error && <Tooltip title={error}>
                <Tag color="warning">数据源部分缺失</Tag>
              </Tooltip>}
              {(evaluationMode === 'rq_only' || evaluationMode === 'cq_only') && <Tooltip
                title="RQ 与 CQ 数据不完整，当前结果仅用于单组试算，不能直接作为完整评价版本应用。"
              >
                <Tag color="warning">仅{evaluationMode === 'rq_only' ? ' RQ' : ' CQ'} 试算</Tag>
              </Tooltip>}
              {reviewView === 'curves' && <Button size="small" onClick={depthInteraction.resetView}>全水平段</Button>}
            </div>
          </div>
          <SweetSpotSummaryCards
            mode={hasEvaluationResult ? 'result' : 'preparation'}
            results={displayResults}
            enabledParameterCount={enabledParameterCount}
            quality={draftRun?.quality ?? previewOutput.quality}
            structuredResult={structuredResult}
            reservoirTypeLabel={reservoirTypeLabel(reservoirType)}
            dataConfidence={dataConfidenceSummary.score}
            confidenceLevel={dataConfidenceSummary.level}
            confidenceReasons={dataConfidenceSummary.reasons}
            engineeringRisk={engineeringRiskSummary.score}
            riskLevel={engineeringRiskSummary.level}
            riskItems={engineeringRiskSummary.items.map((item) => item.message)}
          />

      {reviewView === 'curves' ? <div
        className={`sweetspot-visual-workspace${visualResizeMode ? ' is-resizing' : ''}`}
        style={{ height: visualWorkspaceHeight }}
      >
        <div className="sweetspot-core-visual">
        <div className="sweetspot-visual-panel">
          <div className="sweetspot-visual-title" style={{ color: TH.geo }}>
            测井曲线 · 储层品质 RQ / 工程品质 CQ / 甜点指数 SSI · 五级甜点评价
          </div>
          <div
            className="sweetspot-visual-canvas"
            ref={logChartRef}
            onMouseDown={(event) => {
              if (event.button !== 0) return;
              const rect = event.currentTarget.getBoundingClientRect();
              depthInteraction.startPan(event.clientY, Math.max(1, rect.height - 34));
            }}
            onMouseMove={(event) => {
              if (depthInteraction.isPanningChart || depthInteraction.isDraggingCursor) return;
              const rect = event.currentTarget.getBoundingClientRect();
              depthInteraction.selectAtClientY(event.clientY, { top: rect.top, height: rect.height, plotTop: 34 });
            }}
            title="滚轮缩放 · 拖动平移 · 拖动横向深度游标"
            style={{
              flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden',
              cursor: depthInteraction.isDraggingCursor
                ? 'ns-resize'
                : depthInteraction.isPanningChart ? 'grabbing' : 'grab',
            }}
          >
            {samples.length ? (
              <>
                <LogTracks
                  samples={chartSamples}
                  viewRange={viewRange}
                  mdToTvd={mdToTvd}
                  result={structuredResult}
                  showRecommendations={showRecommendations && hasEvaluationResult}
                  showGradeBackground={showGradeBackground && hasEvaluationResult}
                  showAnomalies={showAnomalies && hasEvaluationResult}
                />
                <DepthCursorOverlay
                  selectedDepth={depthInteraction.selectedDepth}
                  visibleRange={depthInteraction.visibleRange}
                  plotTop={34}
                  onDragStart={(clientY) => {
                    const rect = logChartRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    depthInteraction.startCursorDrag(clientY, {
                      top: rect.top, height: rect.height, plotTop: 34,
                    });
                  }}
                />
              </>
            ) : (
              <div className="sweetspot-empty-state" style={{ minHeight: 0 }}>
                暂无可显示曲线数据
              </div>
            )}
          </div>
        </div>
      </div>

        <div
          className="sweetspot-resize-handle sweetspot-resize-handle--bottom"
          role="separator"
          aria-label="调整图形区高度"
          aria-orientation="horizontal"
          aria-valuemin={MIN_VISUAL_WORKSPACE_HEIGHT}
          aria-valuemax={MAX_VISUAL_WORKSPACE_HEIGHT}
          aria-valuenow={visualWorkspaceHeight}
          tabIndex={0}
          title="上下拖动调整图形区高度，双击恢复默认大小"
          onPointerDown={startVisualResize}
          onDoubleClick={resetVisualWorkspaceBounds}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') adjustVisualWorkspaceHeight(-24);
            else if (event.key === 'ArrowDown') adjustVisualWorkspaceHeight(24);
            else if (event.key === 'Home') resetVisualWorkspaceBounds();
            else return;
            event.preventDefault();
          }}
        />
      </div> : <div className="sweetspot-review-detail-workspace" style={{ height: visualWorkspaceHeight }}>
        {reviewView === 'engineering' ? <SweetSpotResultInspector
          result={structuredResult}
          quality={draftRun?.quality ?? previewOutput.quality}
          run={draftRun}
        /> : <SweetSpotResultTable results={displayResults} height={visualWorkspaceHeight} />}
      </div>}
        </main>

        {reviewView === 'curves' && <aside className="sweetspot-inspector-column" aria-label="当前深度属性">
          <DepthValuePanel
            layout="vertical"
            title="当前深度属性"
            depth={selectedSample?.md ?? null}
            items={selectedDepthItems}
          />
        </aside>}
        </div>}
      </div>

      <footer className="sweetspot-status-bar">
        <span>数据来源：DataHub</span>
        <span>数据完整度 {(previewOutput.quality.completeness * 100).toFixed(1)}%</span>
        <span>缺失参数 {previewOutput.quality.missingIndicatorIds.length} 项</span>
        <span>最近计算：{draftRun ? new Date(draftRun.createdAt).toLocaleString() : '尚未计算'}</span>
      </footer>

      <Modal
        title="切换储层类型"
        open={reservoirModalOpen}
        onCancel={() => { setReservoirModalOpen(false); setPendingReservoirType(null); }}
        footer={null}
        width={560}
      >
        {pendingReservoirType && (() => {
          const nextTemplate = getReservoirTemplate(pendingReservoirType);
          return <div>
            <div style={{ fontSize: 13, color: '#4F6680', lineHeight: 1.7, marginBottom: 14 }}>
              切换储层类型将影响评价参数与模板。当前存在未保存的配置与草稿结果，请选择处理方式；不会静默覆盖当前配置。
            </div>
            <div style={{ marginBottom: 14, fontSize: 12, color: '#8598AD', lineHeight: 1.7 }}>
              <div>当前：{reservoirTypeLabel(reservoirType)}（{reservoirTemplate.rqParameterCodes.length} RQ + {reservoirTemplate.cqParameterCodes.length} CQ）</div>
              <div>目标：{reservoirTypeLabel(pendingReservoirType)}（{nextTemplate.rqParameterCodes.length} RQ + {nextTemplate.cqParameterCodes.length} CQ）</div>
            </div>
            <div className="sweetspot-reservoir-modal-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button type="primary" block onClick={() => applyReservoirType(pendingReservoirType, 'load-template')}>
                加载{reservoirTypeLabel(pendingReservoirType)}默认模板（替换参数）
              </Button>
              <Button block onClick={() => applyReservoirType(pendingReservoirType, 'keep-parameters')}>
                保留当前参数并切换储层类型
              </Button>
              <Button block onClick={() => applyReservoirType(pendingReservoirType, 'keep-parameters')}>
                另存为新评价方案（保留当前参数）
              </Button>
              <Button onClick={() => { setReservoirModalOpen(false); setPendingReservoirType(null); }}>
                取消
              </Button>
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: '#8598AD', lineHeight: 1.6 }}>
              {DEFAULT_EQUAL_WEIGHT_NOTE} {RELATIVE_SCORING_NOTE}
            </div>
          </div>;
        })()}
      </Modal>
    </div>
  );
}

// ── Canvas sizing ──
function useObservedElementSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    let animationFrame = 0;
    const update = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const rect = element.getBoundingClientRect();
        setSize((current) => (
          Math.abs(current.width - rect.width) < 0.5
          && Math.abs(current.height - rect.height) < 0.5
            ? current
            : { width: rect.width, height: rect.height }
        ));
      });
    };

    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(element);
      return () => {
        observer.disconnect();
        window.cancelAnimationFrame(animationFrame);
      };
    }

    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [ref]);

  return size;
}

// ── Multi-track logs vs MD ──
function LogTracks({
  samples, viewRange, mdToTvd, result,
  showRecommendations, showGradeBackground, showAnomalies,
}: {
  samples: any[];
  viewRange: [number, number];
  mdToTvd: (md: number) => number;
  result: SweetSpotEvaluationResult | null;
  showRecommendations: boolean;
  showGradeBackground: boolean;
  showAnomalies: boolean;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const canvasSize = useObservedElementSize(ref);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    cv.width = rect.width * dpr; cv.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const [vT, vB] = viewRange;
    const headerH = 34, axisW = 54;
    const sY = (md: number) => headerH + ((md - vT) / (vB - vT || 1)) * (H - headerH);

    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);

    const vis = samples.filter((s) => s.md >= vT && s.md <= vB);

    if (showRecommendations && result) {
      result.highQualityIntervals.forEach((interval) => {
        const top = Math.max(vT, interval.top);
        const bottom = Math.min(vB, interval.bottom);
        if (bottom <= top) return;
        ctx.fillStyle = 'rgba(30,132,73,0.08)';
        ctx.fillRect(axisW, sY(top), W - axisW, sY(bottom) - sY(top));
      });
    }

    // Track layout
    type Trk = { w: number; title: string; draw: (x: number, w: number) => void };
    const lineTrack = (key: string, color: string, lo: number, hi: number, unit: string) => ({
      w: 86, title: `${key}\n${lo}-${hi}${unit ? ' ' + unit : ''}`,
      draw: (x: number, w: number) => {
        ctx.strokeStyle = color; ctx.lineWidth = 1.4; ctx.beginPath();
        let started = false;
        vis.forEach((s) => {
          const v = s[keyMap[key]];
          if (v == null) { started = false; return; }
          const px = x + norm(v, lo, hi) * w;
          const py = sY(s.md);
          started ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
          started = true;
        });
        ctx.stroke();
      },
    });
    const keyMap: Record<string, string> = {
      GR: 'gr', 'GR（自然伽马）': 'gr', AC: 'ac', RT: 'rt', 'RT（电阻率）': 'rt',
      '孔隙度': 'por', TOC: 'toc', 'TOC（总有机碳）': 'toc',
      '脆性指数': 'brit', 'BI（脆性指数）': 'brit', '杨氏模量': 'youngs', '泊松比': 'poisson',
    };
    const scoreTrack = (key: string, color: string, title: string) => ({
      w: 70, title,
      draw: (x: number, w: number) => {
        // filled bar 0..1
        ctx.fillStyle = color + '22';
        vis.forEach((s, i) => {
          if (i === 0) return;
          const p = s[key]; if (p == null) return;
          const y1 = sY(vis[i - 1].md), y2 = sY(s.md);
          ctx.fillRect(x, y1, p * w, y2 - y1);
        });
        ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.beginPath();
        let started = false;
        vis.forEach((s) => {
          const p = s[key]; if (p == null) { started = false; return; }
          const px = x + p * w, py = sY(s.md);
          started ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
          started = true;
        });
        ctx.stroke();
      },
    });

    const tracks: Trk[] = [
      { w: 70, title: '岩性', draw: (x, w) => {
          vis.forEach((s, i) => {
            if (i === 0 || !s.layer) return;
            const y1 = sY(vis[i - 1].md), y2 = sY(s.md);
            ctx.fillStyle = s.layer.color; ctx.globalAlpha = 0.55;
            ctx.fillRect(x, y1, w, y2 - y1); ctx.globalAlpha = 1;
          });
        } },
      lineTrack('GR（自然伽马）', TH.gr, 0, 200, 'API'),
      lineTrack('AC', TH.ac, 140, 240, 'μs/m'),
      lineTrack('RT（电阻率）', TH.rt, 0, 100, 'Ω·m'),
      lineTrack('孔隙度', TH.por, 0, 10, '%'),
      lineTrack('TOC（总有机碳）', TH.toc, 0, 8, '%'),
      lineTrack('BI（脆性指数）', TH.brit, 0, 1, ''),
      lineTrack('杨氏模量', TH.young, 0, 60, 'GPa'),
      lineTrack('泊松比', TH.poisson, 0.1, 0.35, ''),
      scoreTrack('rq', TH.geo, 'RQ（储层品质）\n0 — 1'),
      scoreTrack('cq', TH.eng, '工程品质 CQ\n0 — 1'),
      scoreTrack('ssi', TH.comp, '甜点指数 SSI\n0 — 1'),
      { w: 64, title: '甜点指数 SSI\n甜点等级', draw: (x, w) => {
          vis.forEach((s, i) => {
            const grade = s.grade as GradeKey | null;
            if (i === 0 || !grade || s.ssi == null) return;
            const y1 = sY(vis[i - 1].md), y2 = sY(s.md);
            if (showGradeBackground) {
              ctx.fillStyle = GRADE[grade].bg;
              ctx.fillRect(x, y1, w, y2 - y1);
            }
            ctx.strokeStyle = GRADE[grade].color; ctx.lineWidth = 1;
            const px = x + s.ssi * w;
            if (i > 1 && vis[i - 1].ssi != null) {
              const pPrev = x + vis[i - 1].ssi * w;
              ctx.beginPath(); ctx.moveTo(pPrev, y1); ctx.lineTo(px, y2); ctx.stroke();
            }
          });
          // grade letters every ~60px
          let last = -999;
          vis.forEach((s) => {
            const grade = s.grade as GradeKey | null;
            if (!grade) return;
            const y = sY(s.md);
            if (y - last < 46) return;
            last = y;
            ctx.fillStyle = GRADE[grade].color;
            ctx.font = 'bold 11px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
            ctx.fillText(GRADE[grade].label, x + w / 2 - 4, y + 4);
          });
        } },
    ];

    // depth axis (MD + TVD)
    const span = vB - vT;
    const st = span > 1500 ? 500 : span > 600 ? 200 : span > 250 ? 100 : 50;
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
    for (let d = Math.ceil(vT / st) * st; d <= vB; d += st) {
      const y = sY(d);
      ctx.beginPath(); ctx.moveTo(axisW, y); ctx.lineTo(W, y); ctx.stroke();
      ctx.fillStyle = TH.textSec; ctx.font = '10px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace'; ctx.fillText(`${d}`, 4, y + 1);
      ctx.fillStyle = TH.textMuted; ctx.font = '8px "Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace'; ctx.fillText(`↧${Math.round(mdToTvd(d))}`, 4, y + 11);
    }
    ctx.fillStyle = TH.textSec; ctx.font = 'bold 9px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.fillText('测深MD', 4, 14);
    ctx.fillStyle = TH.textMuted; ctx.font = '8px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif'; ctx.fillText('↧垂深TVD', 4, 26);

    // draw tracks
    let x = axisW;
    const totalW = tracks.reduce((s, t) => s + t.w, 0);
    const scale = (W - axisW) / totalW;
    tracks.forEach((t) => {
      const w = t.w * scale;
      // column separators + header
      ctx.strokeStyle = TH.headerBorder; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.fillStyle = TH.panelHeaderBg; ctx.fillRect(x, 0, w, headerH);
      ctx.strokeStyle = TH.headerBorder; ctx.beginPath(); ctx.moveTo(x, headerH); ctx.lineTo(x + w, headerH); ctx.stroke();
      ctx.fillStyle = TH.textSec; ctx.font = 'bold 10px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';
      t.title.split('\n').forEach((ln, i) => {
        const tw = ctx.measureText(ln).width;
        ctx.fillText(ln, x + Math.max(3, (w - tw) / 2), 14 + i * 12);
      });
      // clip body
      ctx.save();
      ctx.beginPath(); ctx.rect(x, headerH, w, H - headerH); ctx.clip();
      t.draw(x, w);
      ctx.restore();
      x += w;
    });

    if (showAnomalies && result) {
      result.heterogeneityBreakpoints.forEach((item) => {
        if (item.depth < vT || item.depth > vB) return;
        const y = sY(item.depth);
        ctx.strokeStyle = '#C2533A';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(axisW, y); ctx.lineTo(W, y); ctx.stroke();
        ctx.setLineDash([]);
      });
    }
  }, [
    samples, viewRange, mdToTvd, result, showRecommendations,
    showGradeBackground, showAnomalies, canvasSize.width, canvasSize.height,
  ]);

  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
